'use client'

import {
  closestCenter,
  closestCorners,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  type DraggableAttributes,
  type DraggableSyntheticListeners,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  type PointerActivationConstraint,
  rectIntersection,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Slot } from '@radix-ui/react-slot'
import { ChevronDown, ChevronsLeftRight, GripVertical } from 'lucide-react'
import {
  forwardRef,
  type HTMLAttributes,
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { cn } from '../cn'
import { useIsMobile } from '../hooks/use-is-mobile'

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export interface KanbanColumn<T extends { id: string }> {
  id: string
  /** Displayed in the column header. String or full node. */
  title: ReactNode
  items: T[]
  /** Override item count shown in the header badge. Defaults to items.length. */
  count?: number
  /** Tailwind border class for the top rail (e.g. 'border-blue-500'). */
  accent?: string
  /** Optional icon rendered before the title. */
  icon?: ReactNode
  /** Extra content rendered next to the title/count (e.g. a currency total). */
  headerExtra?: ReactNode
  /** Action buttons rendered on the right of the column header. */
  headerActions?: ReactNode
  /** Per-column empty-state override. Falls back to the board-level `emptyState`. */
  empty?: ReactNode
  /** When true, cards cannot be dragged out of this column (used for read-only/final states). */
  locked?: boolean
  /**
   * Rendered inside the column's vertical-scroll content area, below the
   * last card. Use this for an infinite-scroll sentinel or 'Load N more'
   * button when the column has its own paginated data source.
   */
  footer?: ReactNode
  /**
   * Collapse the column to a narrow vertical strip (title written vertically,
   * count, expand button). The strip is still a drop target: a card dropped on
   * it is appended to the end of the column (in laned mode, to the end of that
   * lane × column cell). Collapsed columns render after all expanded ones on
   * desktop and are omitted on mobile.
   */
  collapsed?: boolean
  /**
   * Items per lane when `lanes` is passed on the board. Keys are lane ids.
   * When omitted, `items` is partitioned via the board-level `laneOf`.
   */
  itemsByLane?: Record<string, T[]>
}

export interface KanbanLane {
  id: string
  title: ReactNode
  /** Override the count shown in the lane header. Defaults to the number of items across all columns. */
  count?: number
  /** Collapsed lanes render only their header row. */
  collapsed?: boolean
  /** Tailwind border class for the lane header's left rail (e.g. 'border-blue-500'). */
  accent?: string
}

export interface KanbanMoveMeta {
  /** IDs participating in a multi-select drag, if the host app tracks one. */
  multiSelectedIds?: Set<string>
  /**
   * Index within the destination column where the card was released.
   * Set on single-card cross-column drops; undefined on multi-select drops
   * (no single dropped index applies when N cards land together).
   */
  newIndex?: number
  /** Lane the card was picked up from. Only set when the board renders `lanes`. */
  fromLaneId?: string
  /** Lane the card was dropped into. Only set when the board renders `lanes`. */
  toLaneId?: string
}

export interface KanbanReorderMeta {
  /** Lane the reorder happened in. Only set when the board renders `lanes`. */
  laneId?: string
}

export interface KanbanBoardProps<T extends { id: string }> {
  columns: KanbanColumn<T>[]
  /** Render one card body. `meta.isDragging` is true for the floating overlay copy. */
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode

  /** Called when a card is dropped into a column. Use this for cross-column moves. */
  onMoveItem?: (itemId: string, fromColumnId: string, toColumnId: string, meta: KanbanMoveMeta) => void
  /**
   * Fired on intra-column drop. Receives the item id, its column id, and the
   * new index within that column. When provided, intra-column drags persist;
   * without it, intra-column drops are visual-only.
   */
  onReorderItem?: (itemId: string, columnId: string, newIndex: number, meta?: KanbanReorderMeta) => void
  /** Async pre-check. Return false to cancel the move. Runs before onMoveItem. */
  onBeforeMove?: (itemId: string, fromColumnId: string, toColumnId: string) => boolean | Promise<boolean>
  /** When provided, column headers show a drag handle on desktop and columns become reorderable. */
  onReorderColumns?: (orderedColumnIds: string[]) => void
  /** Blanket disable for drag-and-drop (cards AND columns). */
  disableDrag?: boolean

  /** Pass-through for multi-select — used by onMoveItem meta and drag-overlay count badge. */
  multiSelectedIds?: Set<string>

  /** Custom overlay rendered while dragging a card. Defaults to a clone of `renderCard`. */
  renderDragOverlay?: (item: T, meta: { multiCount: number }) => ReactNode
  /** Custom overlay rendered while dragging a column (desktop only). */
  renderColumnDragOverlay?: (column: KanbanColumn<T>) => ReactNode

  /** Rendered at the end of the desktop column row (e.g. an "Add section" input). */
  desktopTrailing?: ReactNode

  /** Tailwind class controlling each column's width on desktop. */
  desktopColumnClassName?: string
  /** Fraction of the viewport each column occupies on mobile. Default 0.85 — the remaining 15% becomes symmetric peek. */
  mobileColumnWidthRatio?: number
  /** Dnd-kit collision detection strategy. Default closestCorners (matches Tasker). */
  collisionDetection?: CollisionDetection

  /** Fallback empty-state for columns without their own `empty` override. */
  emptyState?: ReactNode
  /** Extra class on the outermost wrapper. */
  className?: string

  /**
   * Opt-in swimlanes (desktop only; mobile keeps the flat column view). Each
   * lane becomes a full-width row across every column; each lane × column
   * intersection is its own drop cell. Items land in a lane via
   * `column.itemsByLane` or, failing that, `laneOf`. Items whose lane is not
   * listed here are not rendered.
   */
  lanes?: KanbanLane[]
  /** Maps an item to its lane id. Used when a column has no `itemsByLane`. */
  laneOf?: (item: T) => string
  /** Chevron click on a lane header. The host owns `lane.collapsed`. */
  onToggleLane?: (laneId: string) => void
  /**
   * Custom body for a collapsed column strip. Default: vertical title, count,
   * and an expand button that calls `onExpandColumn`.
   */
  renderCollapsedColumn?: (column: KanbanColumn<T>) => ReactNode
  /** Expand-button click on a collapsed column. The host owns `column.collapsed`. */
  onExpandColumn?: (columnId: string) => void
  /**
   * Opt-in "drop onto a card to nest it": while dragging a card with the
   * Alt/Option key held, releasing over another card calls this instead of
   * moving/reordering. The overlay shows a "Make subtask" badge while the
   * modifier is down so the intent is visible before the drop.
   */
  onNestItem?: (itemId: string, targetItemId: string) => void
}

export interface KanbanCardProps extends HTMLAttributes<HTMLDivElement> {
  asChild?: boolean
  isDragging?: boolean
  selected?: boolean
}

export function KanbanCard({ asChild, isDragging, selected, className, ...props }: KanbanCardProps) {
  const Comp = asChild ? Slot : 'div'
  return (
    <Comp
      data-dragging={isDragging ? 'true' : undefined}
      data-selected={selected ? 'true' : undefined}
      className={cn(
        'block rounded-md border bg-background p-3 text-sm shadow-sm transition-[background-color,border-color,box-shadow,transform] duration-150',
        'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        selected && 'border-primary shadow-md',
        isDragging && 'scale-[1.01] shadow-lg',
        className,
      )}
      {...props}
    />
  )
}

export function KanbanColumnEmpty({ children = 'Nothing here.', className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('py-8 text-center text-sm text-muted-foreground', className)} {...props}>
      {children}
    </div>
  )
}

function sameOrder(a: Record<string, string[]>, b: Record<string, string[]>): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) {
    return false
  }
  for (const k of aKeys) {
    const av = a[k]
    const bv = b[k]
    if (!bv || av.length !== bv.length) {
      return false
    }
    for (let i = 0; i < av.length; i++) {
      if (av[i] !== bv[i]) {
        return false
      }
    }
  }
  return true
}

/**
 * Pure drag-over shift logic — exported for unit testing. Returns the new
 * order map (or `prev` to bail) plus a `shiftApplied` flag the caller uses to
 * update its last-shift bookkeeping.
 *
 * The `lastShift` guard breaks the dnd-kit/handleDragOver feedback loop: when
 * an intra-column arrayMove fires for a given (active, over) pair, the
 * post-render layout puts the cursor over the same DOM card whose position
 * just flipped — dnd-kit fires onDragOver again with the same pair, and
 * without the guard the arrayMove would swap them back, then dnd-kit fires
 * again, ad infinitum, tripping React error #185 from inside the dnd-kit
 * chunk.
 */

export type IntraColumnDropOutcome = { action: 'persist'; newIndex: number } | { action: 'revert' }

/**
 * Decide what a within-column drop should do, given whether this board can
 * actually persist a manual order (`canPersist` — i.e. an `onReorderItem`
 * handler is wired).
 *
 * `revert` means: undo the optimistic shift dnd-kit already applied and put
 * the card back where it started.
 *
 * A board without `onReorderItem` is one whose columns are ordered by a
 * field rather than by hand — a Tasker board on any sort except "Manual",
 * say. A hand-placed position there would be discarded on the next read, so
 * the honest response to the drop is to decline it visibly. Leaving the card
 * at the drop site instead (the old behavior) looked like success until an
 * unrelated revalidate quietly moved it back, which reads as the board
 * losing your change rather than refusing it.
 *
 * `newIndex < 0` also reverts: the active card isn't in the column's order,
 * so there is no position to report and persisting would be a guess.
 */
export function intraColumnDropOutcome(
  order: Record<string, string[]>,
  columnId: string,
  activeId: string,
  canPersist: boolean,
): IntraColumnDropOutcome {
  if (!canPersist) {
    return { action: 'revert' }
  }
  const newIndex = order[columnId]?.indexOf(activeId) ?? -1
  return newIndex >= 0 ? { action: 'persist', newIndex } : { action: 'revert' }
}

/**
 * Compute the `disabled` argument for a column-level `useSortable` call.
 *
 * dnd-kit's `useSortable` accepts `disabled` as either a boolean OR
 * `{ draggable?, droppable? }`. The boolean form is short for both being
 * the same value — see `normalizeLocalDisabled` in
 * `@dnd-kit/sortable`. That coercion is the silent footgun that
 * broke drop-on-empty-swimlane (FACTORY-24): passing `disabled: true`
 * disables `droppable` too, so dnd-kit's collision detector cannot see
 * empty columns at all.
 *
 * Always returns the object form with `droppable: false` so the column
 * stays a valid drop target regardless of whether column reordering is
 * allowed in the current role / viewport.
 */
export function columnSortableDisabled(allowDraggable: boolean): { draggable: boolean; droppable: false } {
  return { draggable: !allowDraggable, droppable: false }
}

/**
 * Phase-2 helper for the card-drag collision strategy: returns true when
 * the target column should be returned as the drop target (because it
 * contains no cards other than the one being dragged).
 *
 * Why this is the load-bearing test (FACTORY-24): earlier attempts at
 * "drop on empty column" all failed because the collision strategy
 * scored cards in OTHER (source) columns higher than the empty target
 * column. The two-phase strategy first picks the target column by
 * rect-overlap with the active rect, then asks this helper "are there
 * any landable cards in there?". If no, return the column itself — the
 * drop target lights up as soon as the dragged card crosses the visual
 * midpoint into the target column.
 *
 * Exported so the rule can be locked in by unit test without spinning
 * up jsdom + a full dnd-kit harness.
 */
export function targetColumnIsEmptyForActive(
  targetColumnId: string,
  liveOrder: Record<string, readonly string[]>,
  activeId: string,
): boolean {
  const ids = liveOrder[targetColumnId]
  if (!ids) {
    return true
  }
  for (const id of ids) {
    if (id !== activeId) {
      return false
    }
  }
  return true
}

export function applyDragOverShift(
  prev: Record<string, string[]>,
  activeId: string,
  overId: string,
  lastShift: { activeId: string; overId: string } | null,
): { order: Record<string, string[]>; shiftApplied: boolean } {
  if (lastShift?.activeId === activeId && lastShift?.overId === overId) {
    return { order: prev, shiftApplied: false }
  }
  const activeCol = (Object.keys(prev) as string[]).find((c) => prev[c].includes(activeId))
  if (!activeCol) {
    return { order: prev, shiftApplied: false }
  }
  const overCol = overId in prev ? overId : Object.keys(prev).find((c) => prev[c].includes(overId))
  if (!overCol) {
    return { order: prev, shiftApplied: false }
  }
  if (activeCol === overCol) {
    const fromIndex = prev[activeCol].indexOf(activeId)
    const toIndex = prev[activeCol].indexOf(overId)
    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return { order: prev, shiftApplied: false }
    }
    return {
      order: { ...prev, [activeCol]: arrayMove(prev[activeCol], fromIndex, toIndex) },
      shiftApplied: true,
    }
  }
  const activeItems = prev[activeCol]
  const overItems = prev[overCol]
  const overIndex = overItems.indexOf(overId)
  const insertAt = overIndex >= 0 ? overIndex : overItems.length
  return {
    order: {
      ...prev,
      [activeCol]: activeItems.filter((id) => id !== activeId),
      [overCol]: [...overItems.slice(0, insertAt), activeId, ...overItems.slice(insertAt)],
    },
    shiftApplied: true,
  }
}

/** Separator between lane id and column id in a lane × column cell's droppable id. */
export const CELL_ID_SEPARATOR = '::'

/**
 * Droppable id of a lane × column cell. Lane and column ids may contain any
 * characters except the `::` separator. Pure; exported for unit tests.
 */
export function cellDroppableId(laneId: string, columnId: string): string {
  return `${laneId}${CELL_ID_SEPARATOR}${columnId}`
}

/**
 * Inverse of `cellDroppableId`. Returns null for anything that is not exactly
 * `<laneId>::<columnId>` with both parts non-empty — a plain column id, a card
 * id, an id with more than one separator, or an empty half.
 */
export function parseCellId(id: string): { laneId: string; columnId: string } | null {
  const first = id.indexOf(CELL_ID_SEPARATOR)
  if (first <= 0) {
    return null
  }
  const laneId = id.slice(0, first)
  const columnId = id.slice(first + CELL_ID_SEPARATOR.length)
  if (columnId.length === 0 || columnId.includes(CELL_ID_SEPARATOR)) {
    return null
  }
  return { laneId, columnId }
}

/**
 * Items of one column that belong to one lane. `itemsByLane` wins; otherwise
 * `items` is partitioned by `laneOf`. With neither, the lane gets nothing.
 * Pure; exported for unit tests.
 */
export function laneItemsOf<T extends { id: string }>(
  column: Pick<KanbanColumn<T>, 'items' | 'itemsByLane'>,
  laneId: string,
  laneOf: ((item: T) => string) | undefined,
): T[] {
  const explicit = column.itemsByLane?.[laneId]
  if (explicit) {
    return explicit
  }
  if (!laneOf) {
    return []
  }
  return column.items.filter((item) => laneOf(item) === laneId)
}

/**
 * Desktop render order: expanded columns first, collapsed strips after, each
 * group keeping the caller's relative order. Returns the input array itself
 * when nothing is collapsed so the no-collapse path stays referentially
 * unchanged. Pure; exported for unit tests.
 */
export function orderColumnsForDesktop<T extends { id: string }>(columns: KanbanColumn<T>[]): KanbanColumn<T>[] {
  if (!columns.some((c) => c.collapsed)) {
    return columns
  }
  return [...columns.filter((c) => !c.collapsed), ...columns.filter((c) => c.collapsed)]
}

/* ------------------------------------------------------------------ */
/* Public component                                                   */
/* ------------------------------------------------------------------ */

export function KanbanBoard<T extends { id: string }>(props: KanbanBoardProps<T>) {
  const {
    columns,
    renderCard,
    onMoveItem,
    onReorderItem,
    onBeforeMove,
    onReorderColumns,
    disableDrag,
    multiSelectedIds,
    renderDragOverlay,
    renderColumnDragOverlay,
    desktopTrailing,
    desktopColumnClassName = 'w-[320px]',
    mobileColumnWidthRatio = 0.88,
    collisionDetection: collisionDetectionOverride,
    emptyState,
    className,
    lanes,
    laneOf,
    onToggleLane,
    renderCollapsedColumn,
    onExpandColumn,
    onNestItem,
  } = props
  const baseCollision = collisionDetectionOverride ?? closestCorners

  const isMobile = useIsMobile()
  // Swimlanes are a desktop layout; mobile keeps the flat column view (and
  // therefore column-keyed order + column-level drop targets, exactly as
  // before). Everything lane-aware below keys off this flag, not `lanes`.
  const lanesActive = !!lanes && !isMobile
  // In laned mode the card drop targets are the lane × column cells, not the
  // (sticky, header-only) columns. Ref so `collisionDetection` stays stable.
  const cardDropTypeRef = useRef<'column' | 'cell'>('column')
  cardDropTypeRef.current = lanesActive ? 'cell' : 'column'

  // Type-aware collision detection. When a column is being dragged, filter
  // droppable containers down to columns only — otherwise closestCorners picks
  // the nearest card inside the target column, DragEndEvent.over.id comes back
  // as a task id, `columnIds.indexOf(...)` returns -1, and the column reorder
  // silently no-ops. Card drags keep the default (or caller-overridden) logic.
  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const activeType = (args.active.data.current as { type?: 'card' | 'column' } | undefined)?.type
      if (activeType === 'column') {
        return closestCorners({
          ...args,
          droppableContainers: args.droppableContainers.filter(
            (c) => (c.data.current as { type?: 'card' | 'column' } | undefined)?.type === 'column',
          ),
        })
      }
      // FACTORY-24 — two-phase collision for card drags. Earlier attempts
      // (closestCorners → pointerWithin → rectIntersection + a
      // "cards-over-columns" sort) all had the same family of failure
      // modes around empty columns:
      //   * closestCorners: empty column corners sit at the edges, so
      //     source-column cards always won by closer-corner distance.
      //   * pointerWithin: required the cursor itself to enter the
      //     column — late, since users grab cards near the top-left.
      //   * rectIntersection + "cards beat columns": required clearing
      //     every source-column card's overlap zone before the empty
      //     column would activate (Adam's "had to wiggle the card").
      //
      // Trello/Asana behavior is two-phase:
      //   1) Pick the COLUMN the active is currently "in" — the one
      //      whose rect overlaps the active rect the most. Crossing
      //      happens at the visual midpoint between adjacent columns.
      //   2) Within that column, pick the card whose center is closest
      //      to the active center (the insert anchor handleDragOver
      //      uses). If the column has no cards other than the active
      //      itself, return the COLUMN — that's the empty-swimlane
      //      drop target. The active card is itself a candidate: a
      //      card that has not yet travelled half a slot is closest to
      //      its own resting rect, so `over` is the active and nothing
      //      shifts. Excluding it made the nearest neighbour win at
      //      rest, which swapped the card one slot the instant it was
      //      picked up.
      //
      // Falls back to baseCollision (closestCorners) when phase 1
      // returns nothing — active off-screen / pre-measure / keyboard.
      const cardDropType = cardDropTypeRef.current
      const columnContainers = args.droppableContainers.filter(
        (c) => (c.data.current as { type?: 'card' | 'column' | 'cell' } | undefined)?.type === cardDropType,
      )
      const columnHits = rectIntersection({ ...args, droppableContainers: columnContainers })
      if (columnHits.length === 0) {
        return baseCollision(args)
      }
      const targetColumnId = String(columnHits[0].id)
      const liveOrder = orderRef.current
      const activeIdStr = String(args.active.id)
      if (targetColumnIsEmptyForActive(targetColumnId, liveOrder, activeIdStr)) {
        return [columnHits[0]]
      }
      const cardIdsInTarget = liveOrder[targetColumnId] ?? []
      const cardContainers = args.droppableContainers.filter((c) => {
        const td = (c.data.current as { type?: 'card' | 'column' } | undefined)?.type
        return td === 'card' && (String(c.id) === activeIdStr || cardIdsInTarget.includes(String(c.id)))
      })
      const cardHits = closestCenter({ ...args, droppableContainers: cardContainers })
      if (cardHits.length === 0) {
        return [columnHits[0]]
      }
      return cardHits
    },
    [baseCollision],
  )

  // MouseSensor for fine pointer control on desktop; TouchSensor (delay+tolerance)
  // so touch scroll wins unless the user long-presses. PointerSensor can't express both at once,
  // which is why Tasker's existing board feels finicky on touch.
  //
  // Touch tuning: 250ms / 5px (the original) felt SLOW on drag start and was too sensitive to
  // finger jitter — vertical scroll won even when the user clearly wanted to drag. 200ms / 8px is
  // the sweet spot Asana / Linear settle around: long-press feels prompt, but small accidental
  // movements during the press still land as scroll, not drag.
  //
  // Mouse: see `MOUSE_ACTIVATION_CONSTRAINT`. A card drag starts after a short move in any
  // direction, sideways included.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: MOUSE_ACTIVATION_CONSTRAINT }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const mobileBoardRef = useRef<MobileBoardHandle>(null)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [activeType, setActiveType] = useState<'card' | 'column' | null>(null)
  // Alt/Option held during a card drag → "nest under the card I drop on".
  // Tracked on window so it works whether the key goes down before or during
  // the drag; cleared on every drag end.
  const [nestModifier, setNestModifier] = useState(false)
  const nestArmed = !!onNestItem && activeType === 'card' && nestModifier
  useEffect(() => {
    if (!onNestItem || activeType !== 'card') {
      return
    }
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        setNestModifier(true)
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        setNestModifier(false)
      }
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [onNestItem, activeType])
  // Tracks the last (active, over) pair we already applied a shift for, so a
  // repeat of the same pair (the post-shift layout puts the cursor over the
  // same DOM card whose position just flipped) doesn't trigger a flip-back.
  // Without this, dnd-kit's pointer-driven onDragOver fires 60+ Hz against a
  // shifting layout, the intra-column arrayMove keeps swapping the active
  // card with the same neighbor, and dnd-kit's own state churn trips React
  // error #185 from inside the dnd-kit chunk.
  const lastShiftRef = useRef<{ activeId: string; overId: string } | null>(null)

  // cardId -> item (always from latest props). Survives optimistic reorders.
  const itemById = useMemo(() => {
    const m = new Map<string, T>()
    for (const col of columns) {
      for (const it of col.items) {
        m.set(it.id, it)
      }
      if (col.itemsByLane) {
        for (const laneItems of Object.values(col.itemsByLane)) {
          for (const it of laneItems) {
            m.set(it.id, it)
          }
        }
      }
    }
    return m
  }, [columns])

  // Desktop render order: expanded columns first, collapsed strips last. The
  // same array when nothing is collapsed, so existing boards see no change.
  const orderedColumns = useMemo(() => (isMobile ? columns : orderColumnsForDesktop(columns)), [columns, isMobile])
  const columnIds = useMemo(() => orderedColumns.map((c) => c.id), [orderedColumns])

  // Every drop container the working order is keyed by: column ids, or in
  // laned mode one `cellDroppableId` per lane × column. `resolveContainer`
  // maps a key back to its column (and lane).
  const containerKeys = useMemo<Array<{ key: string; columnId: string; laneId?: string }>>(() => {
    if (!lanesActive || !lanes) {
      return columns.map((col) => ({ key: col.id, columnId: col.id }))
    }
    const keys: Array<{ key: string; columnId: string; laneId?: string }> = []
    for (const lane of lanes) {
      for (const col of columns) {
        keys.push({ key: cellDroppableId(lane.id, col.id), columnId: col.id, laneId: lane.id })
      }
    }
    return keys
  }, [columns, lanes, lanesActive])
  const resolveContainer = useCallback(
    (key: string): { columnId: string; laneId?: string } => {
      if (!lanesActive) {
        return { columnId: key }
      }
      const parsed = parseCellId(key)
      return parsed ? { columnId: parsed.columnId, laneId: parsed.laneId } : { columnId: key }
    },
    [lanesActive],
  )
  const itemsOfContainer = useCallback(
    (key: string): T[] => {
      const col = columns.find((c) => c.id === key)
      if (col) {
        return col.items
      }
      const parsed = parseCellId(key)
      const laneCol = parsed ? columns.find((c) => c.id === parsed.columnId) : undefined
      return parsed && laneCol ? laneItemsOf(laneCol, parsed.laneId, laneOf) : []
    },
    [columns, laneOf],
  )

  // cardId -> original container key (column id, or lane × column cell id in
  // laned mode) from the last settled props. Used to compute (from, to) for
  // onMoveItem after an optimistic cross-container shift.
  const originalColumnOf = useMemo(() => {
    const m = new Map<string, string>()
    for (const { key } of containerKeys) {
      for (const it of itemsOfContainer(key)) {
        m.set(it.id, key)
      }
    }
    return m
  }, [containerKeys, itemsOfContainer])

  // Working ordering per container — mutated optimistically on drag-over so
  // other cards visibly shift to open a slot for the active card. Resets from
  // props whenever a drag isn't in flight.
  const buildOrder = useCallback(() => {
    const r: Record<string, string[]> = {}
    for (const { key } of containerKeys) {
      r[key] = itemsOfContainer(key).map((i) => i.id)
    }
    return r
  }, [containerKeys, itemsOfContainer])
  const [order, setOrder] = useState<Record<string, string[]>>(buildOrder)
  // FACTORY-24 — collisionDetection (defined below) needs the live order
  // map to determine which column the active card is currently in. A ref
  // keeps the callback identity stable while still reading fresh state.
  const orderRef = useRef(order)
  useEffect(() => {
    orderRef.current = order
  }, [order])
  useEffect(() => {
    if (activeId) {
      return
    }
    // Content-aware reseed: skip the state update when the rebuilt order
    // matches the existing one column-for-column. Without this, any parent
    // that hands us a new `columns` ref every render (e.g. an upstream render
    // storm) feeds an infinite `setOrder → re-render → setOrder` loop, which
    // during a drag piles state updates onto dnd-kit and trips React's
    // "Maximum update depth" guard from inside @dnd-kit/core.
    setOrder((prev) => {
      const next = buildOrder()
      return sameOrder(prev, next) ? prev : next
    })
  }, [activeId, buildOrder])

  const findColumnForId = useCallback(
    (id: string): string | null => {
      if (id in order) {
        return id
      }
      for (const [colId, ids] of Object.entries(order)) {
        if (ids.includes(id)) {
          return colId
        }
      }
      return null
    },
    [order],
  )

  // Order as it stood when the drag began, so a drop can tell "nothing moved"
  // apart from "moved and came back", independent of whether the host has
  // already reflected an earlier reorder in props.
  const dragStartOrderRef = useRef<Record<string, string[]> | null>(null)

  const handleDragStart = (event: DragStartEvent) => {
    const type = (event.active.data.current?.type as 'card' | 'column' | undefined) ?? 'card'
    setActiveId(String(event.active.id))
    setActiveType(type)
    lastShiftRef.current = null
    dragStartOrderRef.current = orderRef.current
  }

  const handleDragOver = (event: DragOverEvent) => {
    if (activeType !== 'card') {
      return
    }
    const { active, over } = event
    if (!over) {
      return
    }
    const activeIdStr = String(active.id)
    const overIdStr = String(over.id)
    if (activeIdStr === overIdStr) {
      return
    }
    let appliedThisPass = false
    setOrder((prev) => {
      const { order: next, shiftApplied } = applyDragOverShift(prev, activeIdStr, overIdStr, lastShiftRef.current)
      appliedThisPass = shiftApplied
      return next
    })
    if (appliedThisPass) {
      lastShiftRef.current = { activeId: activeIdStr, overId: overIdStr }
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    const type = activeType
    const activeIdStr = String(active.id)
    setActiveId(null)
    setActiveType(null)
    lastShiftRef.current = null

    // Capture origin index BEFORE any early return — used to scroll the
    // mobile viewport back to the source column on rejected/aborted drops.
    const originIdxForScroll =
      type === 'card' ? columnIds.indexOf(resolveContainer(originalColumnOf.get(activeIdStr) ?? '').columnId) : -1
    const restoreScroll = (idx: number) => {
      if (idx >= 0) {
        mobileBoardRef.current?.scrollToColumn(idx)
      }
    }

    const wasNestArmed = nestArmed
    setNestModifier(false)

    if (!over) {
      setOrder(buildOrder())
      restoreScroll(originIdxForScroll)
      return
    }

    // Alt-drop onto another card: nest, don't move. The optimistic column
    // shift from handleDragOver is reverted — the card stays where it was.
    if (type === 'card' && wasNestArmed && onNestItem) {
      const targetId = String(over.id)
      if (targetId !== activeIdStr && itemById.has(targetId)) {
        setOrder(buildOrder())
        restoreScroll(originIdxForScroll)
        onNestItem(activeIdStr, targetId)
        return
      }
    }

    // Column reorder (desktop only — the sortable wrapper is disabled on mobile).
    if (type === 'column') {
      if (!onReorderColumns) {
        return
      }
      // Belt-and-suspenders: if a caller overrides `collisionDetection` and
      // over.id still lands on a card, resolve it back to its column via the
      // card→column map we already maintain.
      let overId = String(over.id)
      if (!columnIds.includes(overId)) {
        const container = findColumnForId(overId)
        overId = container ? resolveContainer(container).columnId : overId
      }
      if (active.id === overId) {
        return
      }
      const oldIndex = columnIds.indexOf(activeIdStr)
      const newIndex = columnIds.indexOf(overId)
      if (oldIndex === -1 || newIndex === -1) {
        return
      }
      onReorderColumns(arrayMove(columnIds, oldIndex, newIndex))
      return
    }

    if (!onMoveItem) {
      setOrder(buildOrder())
      restoreScroll(originIdxForScroll)
      return
    }

    // Container keys: plain column ids, or lane × column cell ids in laned mode.
    const originKey = originalColumnOf.get(activeIdStr)
    const targetKey = findColumnForId(activeIdStr)
    if (!originKey || !targetKey) {
      setOrder(buildOrder())
      restoreScroll(originIdxForScroll)
      return
    }
    const origin = resolveContainer(originKey)
    const target = resolveContainer(targetKey)
    const targetIdxForScroll = columnIds.indexOf(target.columnId)

    const meta: KanbanMoveMeta = { multiSelectedIds }
    if (lanesActive) {
      meta.toLaneId = target.laneId
    }

    // Multi-select: move every selected card that isn't already in the target.
    if (multiSelectedIds && multiSelectedIds.size > 1 && multiSelectedIds.has(activeIdStr)) {
      for (const id of multiSelectedIds) {
        const fromKey = originalColumnOf.get(id)
        if (!fromKey || fromKey === targetKey) {
          continue
        }
        const from = resolveContainer(fromKey)
        if (onBeforeMove) {
          const ok = await onBeforeMove(id, from.columnId, target.columnId)
          if (!ok) {
            continue
          }
        }
        onMoveItem(id, from.columnId, target.columnId, lanesActive ? { ...meta, fromLaneId: from.laneId } : meta)
      }
      // Multi-select drops always end on the target column.
      restoreScroll(targetIdxForScroll)
      return
    }

    if (originKey === targetKey) {
      if (dragStartOrderRef.current && sameOrder(order, dragStartOrderRef.current)) {
        // Released where it started (e.g. dragged away and back). Nothing to persist.
        restoreScroll(originIdxForScroll)
        return
      }
      const outcome = intraColumnDropOutcome(order, targetKey, activeIdStr, Boolean(onReorderItem))
      if (outcome.action === 'revert') {
        setOrder(buildOrder())
        restoreScroll(originIdxForScroll)
        return
      }
      if (lanesActive) {
        onReorderItem?.(activeIdStr, target.columnId, outcome.newIndex, { laneId: target.laneId })
      } else {
        onReorderItem?.(activeIdStr, target.columnId, outcome.newIndex)
      }
      restoreScroll(targetIdxForScroll)
      return
    }
    if (onBeforeMove) {
      const ok = await onBeforeMove(activeIdStr, origin.columnId, target.columnId)
      if (!ok) {
        setOrder(buildOrder())
        restoreScroll(originIdxForScroll)
        return
      }
    }
    // The local `order` state already has the active card spliced into the
    // destination container at the dropped slot (handleDragOver maintained
    // that live during the drag). Reading the index here gives the consumer
    // the exact landing position, so cross-column drops don't slam to position 0.
    const droppedIndex = order[targetKey]?.indexOf(activeIdStr) ?? -1
    const enrichedMeta: KanbanMoveMeta = droppedIndex >= 0 ? { ...meta, newIndex: droppedIndex } : { ...meta }
    if (lanesActive) {
      enrichedMeta.fromLaneId = origin.laneId
    }
    // Same column, different lane still goes through onMoveItem (from === to
    // column id; the lane ids in meta carry the actual change).
    onMoveItem(activeIdStr, origin.columnId, target.columnId, enrichedMeta)
    // Cross-column success: settle on the destination so snap-mandatory
    // doesn't yank the viewport back to a closer-aligned (origin) column.
    restoreScroll(targetIdxForScroll)
  }

  // Columns resolved through the working order so intra-drag rearrangements
  // render instantly. Falls back to props ordering outside a drag.
  const displayColumns = useMemo<KanbanColumn<T>[]>(
    () =>
      orderedColumns.map((col) => {
        const ids = order[col.id] ?? col.items.map((i) => i.id)
        const items = ids.map((id) => itemById.get(id)).filter((x): x is T => !!x)
        return { ...col, items }
      }),
    [orderedColumns, order, itemById],
  )

  // Laned mode: items per lane × column cell, resolved through the working
  // order the same way `displayColumns` is.
  const displayCells = useMemo<Record<string, T[]>>(() => {
    if (!lanesActive) {
      return {}
    }
    const cells: Record<string, T[]> = {}
    for (const { key } of containerKeys) {
      const ids = order[key] ?? itemsOfContainer(key).map((i) => i.id)
      cells[key] = ids.map((id) => itemById.get(id)).filter((x): x is T => !!x)
    }
    return cells
  }, [lanesActive, containerKeys, order, itemById, itemsOfContainer])

  const activeCard = activeId && activeType === 'card' ? (itemById.get(activeId) ?? null) : null
  const activeColumn = activeId && activeType === 'column' ? (columns.find((c) => c.id === activeId) ?? null) : null

  const multiCount =
    activeCard && multiSelectedIds && multiSelectedIds.size > 1 && multiSelectedIds.has(activeCard.id)
      ? multiSelectedIds.size
      : 0

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={(event) => {
        setActiveId(null)
        setActiveType(null)
        lastShiftRef.current = null
        setOrder(buildOrder())
        // Cancel = revert; settle the mobile viewport on the source column.
        const originKey = originalColumnOf.get(String(event.active.id))
        const idx = originKey ? columnIds.indexOf(resolveContainer(originKey).columnId) : -1
        if (idx >= 0) {
          mobileBoardRef.current?.scrollToColumn(idx)
        }
      }}>
      {isMobile ? (
        <MobileBoard
          ref={mobileBoardRef}
          columns={displayColumns.filter((c) => !c.collapsed)}
          columnIds={columnIds}
          renderCard={renderCard}
          emptyState={emptyState}
          activeId={activeId}
          mobileColumnWidthRatio={mobileColumnWidthRatio}
          disableDrag={disableDrag}
          className={className}
        />
      ) : lanesActive && lanes ? (
        <DesktopLanedBoard
          columns={orderedColumns}
          columnIds={columnIds}
          lanes={lanes}
          cells={displayCells}
          renderCard={renderCard}
          emptyState={emptyState}
          desktopColumnClassName={desktopColumnClassName}
          desktopTrailing={desktopTrailing}
          enableColumnReorder={!!onReorderColumns && !disableDrag}
          disableDrag={disableDrag}
          onToggleLane={onToggleLane}
          onExpandColumn={onExpandColumn}
          renderCollapsedColumn={renderCollapsedColumn}
          className={className}
        />
      ) : (
        <DesktopBoard
          columns={displayColumns}
          columnIds={columnIds}
          renderCard={renderCard}
          emptyState={emptyState}
          desktopColumnClassName={desktopColumnClassName}
          desktopTrailing={desktopTrailing}
          enableColumnReorder={!!onReorderColumns && !disableDrag}
          disableDrag={disableDrag}
          onExpandColumn={onExpandColumn}
          renderCollapsedColumn={renderCollapsedColumn}
          className={className}
        />
      )}
      {/* dropAnimation={null}: cards already moved optimistically in handleDragOver,
        so the default snap-back animation runs against stale rects and visually
        bounces the ghost back to the origin column on successful drops (03d3c2a3). */}
      <DragOverlay dropAnimation={null}>
        {activeCard &&
          (renderDragOverlay ? (
            renderDragOverlay(activeCard, { multiCount })
          ) : (
            <DefaultCardOverlay count={multiCount} nest={nestArmed}>
              {renderCard(activeCard, { isDragging: true })}
            </DefaultCardOverlay>
          ))}
        {activeColumn &&
          (renderColumnDragOverlay ? (
            renderColumnDragOverlay(activeColumn)
          ) : (
            <div className="rounded-xl border border-border bg-surface-sunken px-3 py-2 shadow-lg">
              <span className="text-sm font-semibold">{activeColumn.title}</span>
            </div>
          ))}
      </DragOverlay>
    </DndContext>
  )
}

function DefaultCardOverlay({ count, nest, children }: { count: number; nest?: boolean; children: ReactNode }) {
  return (
    <div className="relative rotate-[0.5deg] scale-[1.02] shadow-xl">
      {children}
      {nest && (
        <div className="absolute -bottom-2 left-2 rounded-full bg-primary px-2 py-0.5 text-[11px] font-medium text-primary-foreground shadow">
          Drop on a card to make it a subtask
        </div>
      )}
      {count > 1 && (
        <div className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground shadow">
          {count}
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Desktop layout                                                     */
/* ------------------------------------------------------------------ */

interface DesktopBoardProps<T extends { id: string }> {
  columns: KanbanColumn<T>[]
  columnIds: string[]
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  desktopColumnClassName: string
  desktopTrailing?: ReactNode
  enableColumnReorder: boolean
  disableDrag?: boolean
  onExpandColumn?: (columnId: string) => void
  renderCollapsedColumn?: (column: KanbanColumn<T>) => ReactNode
  className?: string
}

function DesktopBoard<T extends { id: string }>({
  columns,
  columnIds,
  renderCard,
  emptyState,
  desktopColumnClassName,
  desktopTrailing,
  enableColumnReorder,
  disableDrag,
  onExpandColumn,
  renderCollapsedColumn,
  className,
}: DesktopBoardProps<T>) {
  // `columns` already arrive expanded-first, collapsed-last (see
  // `orderColumnsForDesktop`); a collapsed column renders as a narrow strip
  // that is still a drop target.
  const columnList = (
    <>
      {columns.map((column) =>
        column.collapsed ? (
          <CollapsedColumnStrip
            key={column.id}
            droppableId={column.id}
            droppableType="column"
            column={column}
            onExpandColumn={onExpandColumn}
            renderCollapsedColumn={renderCollapsedColumn}
          />
        ) : (
          <DesktopColumn
            key={column.id}
            column={column}
            renderCard={renderCard}
            emptyState={emptyState}
            columnClassName={desktopColumnClassName}
            showDragHandle={enableColumnReorder}
            disableDrag={disableDrag}
          />
        ),
      )}
      {desktopTrailing && <div className={cn('flex-shrink-0', desktopColumnClassName)}>{desktopTrailing}</div>}
    </>
  )

  const scrollerRef = useRef<HTMLDivElement>(null)
  useDesktopBoardScroller(scrollerRef)

  const scroller = cn('flex h-full gap-3 overflow-x-auto overscroll-x-contain scrollbar-auto pt-2 px-4', className)

  if (enableColumnReorder) {
    return (
      <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
        <div ref={scrollerRef} className={scroller}>
          {columnList}
        </div>
      </SortableContext>
    )
  }
  return (
    <div ref={scrollerRef} className={scroller}>
      {columnList}
    </div>
  )
}

/**
 * The desktop board's horizontal-scroll behaviors, shared by the flat and the
 * laned layouts: trackpad/wheel forwarding, overflow reporting for the mouse
 * sensor, and page-wide mouse drag-to-pan.
 */
function useDesktopBoardScroller(scrollerRef: { current: HTMLDivElement | null }) {
  // Trackpad / wheel: forward the horizontal component to scrollLeft. Each
  // column's content area is `overflow-y-auto`, and the browser latches a wheel
  // gesture to the scroller under the cursor — so once a gesture has started
  // scrolling a column vertically, its sideways component is dropped instead of
  // panning the board. Forwarding it here keeps the board moving.
  //
  // BUT: trackpads also emit tiny accidental deltaX during vertical scrolls.
  // Forwarding every nonzero deltaX makes the board jitter sideways while the
  // user is reading down a column (17cd92a8). Axis resolution lives in
  // `resolveWheelAxis`: clear horizontal intent starts a pan, an axis is held
  // for 100ms after its last event so a steady gesture doesn't flicker, and a
  // decisively contrary gesture flips the axis instead of waiting the lock out.
  // The earlier version re-armed the vertical lock on EVERY event with any
  // deltaY — a sideways swipe carrying 1px of deltaY kept the board locked
  // vertical for as long as the swipe lasted, which read as "the board is
  // stuck after scrolling a column". shift+wheel routes to horizontal.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) {
      return
    }
    let axisLock: WheelAxis = null
    let axisLockTimer: ReturnType<typeof setTimeout> | null = null
    const setAxis = (axis: 'x' | 'y') => {
      axisLock = axis
      if (axisLockTimer) {
        clearTimeout(axisLockTimer)
      }
      axisLockTimer = setTimeout(() => {
        axisLock = null
      }, WHEEL_AXIS_LOCK_MS)
    }
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        return
      }
      const dx = e.shiftKey && e.deltaX === 0 ? e.deltaY : e.deltaX
      const dy = e.shiftKey && e.deltaX === 0 ? 0 : e.deltaY
      const axis = resolveWheelAxis(axisLock, dx, dy)
      if (axis === 'x') {
        el.scrollLeft += dx
        e.preventDefault()
      }
      if (axis) {
        setAxis(axis)
      }
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => {
      el.removeEventListener('wheel', onWheel)
      if (axisLockTimer) {
        clearTimeout(axisLockTimer)
      }
    }
  }, [scrollerRef])

  // Mouse drag-to-pan, page-wide. Press the mouse anywhere on the page that is
  // not a control (button, link, field, dialog, side panel, column handle) or a
  // drag-enabled card, and drag sideways to scroll the board. Trackpads already
  // get this via the wheel forwarder above; a plain mouse has no horizontal
  // scroll at all without this. Listeners live on the document so the gesture
  // works from the page header and gutters, not only from inside the board
  // scroller. Once the pointer moves past BOARD_PAN_ACTIVATION_PX the gesture is
  // a pan: the click the browser fires on release is swallowed (capture-phase,
  // document level) so it can never open a card.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) {
      return
    }
    let startX = 0
    let startScrollLeft = 0
    let pointerId: number | null = null
    let panning = false
    // The click the browser synthesizes after a pan is swallowed. It normally
    // arrives in the same input task as the pointerup, but the listener is
    // kept for a short grace period (dnd-kit uses the same 50ms for its own
    // post-drag click guard) rather than exactly one macrotask, so a click
    // that is delivered a tick late still cannot open a card. No human can
    // start a new click that fast, so nothing legitimate is lost.
    let swallowTimer: ReturnType<typeof setTimeout> | null = null
    const stopSwallowing = () => {
      document.removeEventListener('click', swallowClick, { capture: true })
      if (swallowTimer) {
        clearTimeout(swallowTimer)
        swallowTimer = null
      }
    }
    const swallowClick = (e: MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      stopSwallowing()
    }
    const cleanup = () => {
      pointerId = null
      if (panning) {
        panning = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        stopSwallowing()
        document.addEventListener('click', swallowClick, { capture: true })
        swallowTimer = setTimeout(stopSwallowing, BOARD_PAN_CLICK_GUARD_MS)
      }
    }
    const onDown = (e: PointerEvent) => {
      if (!shouldStartBoardPan(e, el)) {
        return
      }
      pointerId = e.pointerId
      startX = e.clientX
      startScrollLeft = el.scrollLeft
    }
    const onMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) {
        return
      }
      const dx = startX - e.clientX
      if (!panning) {
        if (Math.abs(dx) <= BOARD_PAN_ACTIVATION_PX) {
          return
        }
        panning = true
        document.body.style.cursor = 'grabbing'
        document.body.style.userSelect = 'none'
      }
      el.scrollLeft = startScrollLeft + dx
      e.preventDefault()
    }
    const onUp = (e: PointerEvent) => {
      if (pointerId === e.pointerId) {
        cleanup()
      }
    }
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onUp)
    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onUp)
      cleanup()
      stopSwallowing()
    }
  }, [scrollerRef])
}

/* ------------------------------------------------------------------ */
/* Desktop layout — swimlanes                                         */
/* ------------------------------------------------------------------ */

/** Width class of a collapsed column strip (both layouts). */
export const COLLAPSED_COLUMN_CLASS = 'w-10'

interface DesktopLanedBoardProps<T extends { id: string }> {
  columns: KanbanColumn<T>[]
  columnIds: string[]
  lanes: KanbanLane[]
  /** Items per `cellDroppableId(lane, column)`, already resolved through the working order. */
  cells: Record<string, T[]>
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  desktopColumnClassName: string
  desktopTrailing?: ReactNode
  enableColumnReorder: boolean
  disableDrag?: boolean
  onToggleLane?: (laneId: string) => void
  onExpandColumn?: (columnId: string) => void
  renderCollapsedColumn?: (column: KanbanColumn<T>) => ReactNode
  className?: string
}

/**
 * Swimlane layout: one CSS grid, a sticky header row of column headers, then
 * per lane a full-width lane header row followed (unless collapsed) by one
 * droppable cell per column. Every track sizes to its content, so the cells
 * carry the same width classes as the flat board's columns.
 */
function DesktopLanedBoard<T extends { id: string }>({
  columns,
  columnIds,
  lanes,
  cells,
  renderCard,
  emptyState,
  desktopColumnClassName,
  desktopTrailing,
  enableColumnReorder,
  disableDrag,
  onToggleLane,
  onExpandColumn,
  renderCollapsedColumn,
  className,
}: DesktopLanedBoardProps<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  useDesktopBoardScroller(scrollerRef)

  const trackCount = columns.length + (desktopTrailing ? 1 : 0)

  const laneCount = (lane: KanbanLane): number => {
    if (lane.count !== undefined) {
      return lane.count
    }
    let n = 0
    for (const column of columns) {
      n += cells[cellDroppableId(lane.id, column.id)]?.length ?? 0
    }
    return n
  }

  const headerRow = (
    <>
      {columns.map((column) => (
        <LanedColumnHeader
          key={column.id}
          column={column}
          columnClassName={desktopColumnClassName}
          showDragHandle={enableColumnReorder && !column.collapsed}
          onExpandColumn={onExpandColumn}
          renderCollapsedColumn={renderCollapsedColumn}
        />
      ))}
      {desktopTrailing && (
        <div className={cn(LANED_HEADER_WRAPPER_CLASS, desktopColumnClassName)}>{desktopTrailing}</div>
      )}
    </>
  )

  const grid = (
    <div ref={scrollerRef} className={cn('h-full overflow-auto overscroll-x-contain scrollbar-auto px-4', className)}>
      <div
        className="grid min-w-max items-stretch gap-x-3"
        style={{ gridTemplateColumns: `repeat(${trackCount}, max-content)` }}>
        {headerRow}
        {lanes.map((lane) => (
          <LaneRows
            key={lane.id}
            lane={lane}
            count={laneCount(lane)}
            columns={columns}
            cells={cells}
            renderCard={renderCard}
            emptyState={emptyState}
            desktopColumnClassName={desktopColumnClassName}
            disableDrag={disableDrag}
            onToggleLane={onToggleLane}
            renderCollapsedColumn={renderCollapsedColumn}
          />
        ))}
      </div>
    </div>
  )

  if (enableColumnReorder) {
    return (
      <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
        {grid}
      </SortableContext>
    )
  }
  return grid
}

/**
 * Sticky header cells of the laned grid carry the page background and the
 * row's vertical padding themselves: the scroller has no top padding and the
 * grid no row gap, so there is no strip above or below the header row for
 * scrolled cards (or a lane header) to show through.
 */
const LANED_HEADER_WRAPPER_CLASS = 'sticky top-0 z-10 self-start bg-background pt-2 pb-2'

interface LanedColumnHeaderProps<T extends { id: string }> {
  column: KanbanColumn<T>
  columnClassName: string
  showDragHandle: boolean
  onExpandColumn?: (columnId: string) => void
  renderCollapsedColumn?: (column: KanbanColumn<T>) => ReactNode
}

/** Sticky column header cell of the laned grid; the column-reorder sortable lives here. */
function LanedColumnHeader<T extends { id: string }>({
  column,
  columnClassName,
  showDragHandle,
  onExpandColumn,
  renderCollapsedColumn,
}: LanedColumnHeaderProps<T>) {
  // Droppable off: in laned mode the cells are the card drop targets, and
  // column-drag collision is filtered to type 'column' anyway.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: { type: 'column' },
    disabled: columnSortableDisabled(showDragHandle),
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  const count = column.count ?? column.items.length

  if (column.collapsed) {
    // Compact on purpose: a vertical title here would stretch the whole
    // header row to its height. The title is written in each lane's cell.
    return (
      <div
        ref={setNodeRef}
        style={style}
        id={column.id}
        className={cn(LANED_HEADER_WRAPPER_CLASS, COLLAPSED_COLUMN_CLASS)}>
        <div
          className={cn(
            'flex flex-col items-center rounded-t-xl bg-surface-sunken pb-2',
            column.accent && `border-t-4 ${column.accent}`,
          )}
          title={typeof column.title === 'string' ? column.title : undefined}>
          {renderCollapsedColumn ? (
            renderCollapsedColumn(column)
          ) : (
            <CollapsedColumnBody count={count} onExpand={onExpandColumn && (() => onExpandColumn(column.id))} />
          )}
        </div>
      </div>
    )
  }

  return (
    <div ref={setNodeRef} style={style} id={column.id} className={cn(LANED_HEADER_WRAPPER_CLASS, columnClassName)}>
      <div className={cn('rounded-t-xl bg-surface-sunken', column.accent && `border-t-4 ${column.accent}`)}>
        <KanbanColumnHeader
          title={column.title}
          icon={column.icon}
          count={count}
          headerExtra={column.headerExtra}
          headerActions={column.headerActions}
          dragAttributes={showDragHandle ? attributes : undefined}
          dragListeners={showDragHandle ? listeners : undefined}
        />
      </div>
    </div>
  )
}

interface LaneRowsProps<T extends { id: string }> {
  lane: KanbanLane
  count: number
  columns: KanbanColumn<T>[]
  cells: Record<string, T[]>
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  desktopColumnClassName: string
  disableDrag?: boolean
  onToggleLane?: (laneId: string) => void
  renderCollapsedColumn?: (column: KanbanColumn<T>) => ReactNode
}

/** One lane: its full-width header row, then (unless collapsed) a cell per column. */
function LaneRows<T extends { id: string }>({
  lane,
  count,
  columns,
  cells,
  renderCard,
  emptyState,
  desktopColumnClassName,
  disableDrag,
  onToggleLane,
  renderCollapsedColumn,
}: LaneRowsProps<T>) {
  return (
    <>
      <div
        data-kanban-lane={lane.id}
        className={cn(
          'mb-2 flex items-center rounded-md bg-muted/50 py-1.5 pr-3',
          lane.accent ? `border-l-4 pl-2 ${lane.accent}` : 'pl-2',
        )}
        style={{ gridColumn: '1 / -1' }}>
        {/* Pinned to the left edge of the scroller so the lane name stays in view while panning. */}
        <div className="sticky left-4 flex min-w-0 items-center gap-1.5">
          <button
            type="button"
            aria-label={lane.collapsed ? 'Expand lane' : 'Collapse lane'}
            aria-expanded={!lane.collapsed}
            onClick={onToggleLane ? () => onToggleLane(lane.id) : undefined}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <ChevronDown className={cn('h-4 w-4 transition-transform duration-150', lane.collapsed && '-rotate-90')} />
          </button>
          <div className="truncate text-xs font-semibold uppercase tracking-wide">{lane.title}</div>
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{count}</span>
        </div>
      </div>
      {!lane.collapsed &&
        columns.map((column) => {
          const id = cellDroppableId(lane.id, column.id)
          if (column.collapsed) {
            return (
              <CollapsedColumnStrip
                key={id}
                droppableId={id}
                droppableType="cell"
                column={column}
                count={cells[id]?.length ?? 0}
                renderCollapsedColumn={renderCollapsedColumn}
                bare
              />
            )
          }
          return (
            <LaneCell
              key={id}
              id={id}
              column={column}
              items={cells[id] ?? []}
              renderCard={renderCard}
              emptyState={emptyState}
              columnClassName={desktopColumnClassName}
              disableDrag={disableDrag}
            />
          )
        })}
    </>
  )
}

interface LaneCellProps<T extends { id: string }> {
  id: string
  column: KanbanColumn<T>
  items: T[]
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  columnClassName: string
  disableDrag?: boolean
}

/** One lane × column drop cell with its own sortable list of cards. */
function LaneCell<T extends { id: string }>({
  id,
  column,
  items,
  renderCard,
  emptyState,
  columnClassName,
  disableDrag,
}: LaneCellProps<T>) {
  // Object-form `disabled` — see `columnSortableDisabled` / FACTORY-24: the
  // cell must stay droppable so an empty cell is a valid target.
  const { setNodeRef } = useSortable({
    id,
    data: { type: 'cell' },
    disabled: columnSortableDisabled(false),
  })
  const itemIds = items.map((item) => item.id)
  return (
    <div
      ref={setNodeRef}
      data-kanban-cell={id}
      className={cn('mb-2 flex min-h-[72px] flex-col rounded-md bg-surface-sunken', columnClassName)}>
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div className="flex-1 space-y-2 p-3">
          {items.length === 0
            ? (column.empty ?? emptyState ?? null)
            : items.map((item) => (
                <SortableCard key={item.id} id={item.id} disabled={disableDrag || column.locked}>
                  {(meta) => renderCard(item, meta)}
                </SortableCard>
              ))}
        </div>
      </SortableContext>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Collapsed column strip                                             */
/* ------------------------------------------------------------------ */

interface CollapsedColumnStripProps<T extends { id: string }> {
  /** Droppable id: the column id (flat board) or a lane × column cell id (laned board). */
  droppableId: string
  droppableType: 'column' | 'cell'
  column: KanbanColumn<T>
  /** Count shown in the strip. Defaults to the column's count. */
  count?: number
  onExpandColumn?: (columnId: string) => void
  renderCollapsedColumn?: (column: KanbanColumn<T>) => ReactNode
  /** Laned cells: the header row already shows title + expand, so the strip is just a drop surface. */
  bare?: boolean
}

/**
 * A collapsed column: a narrow vertical strip that is still a drop target.
 * Cards are not rendered inside it, so the collision strategy resolves a drop
 * to the strip itself and `applyDragOverShift` appends to the end.
 */
function CollapsedColumnStrip<T extends { id: string }>({
  droppableId,
  droppableType,
  column,
  count,
  onExpandColumn,
  renderCollapsedColumn,
  bare,
}: CollapsedColumnStripProps<T>) {
  const { setNodeRef, isOver } = useSortable({
    id: droppableId,
    data: { type: droppableType },
    disabled: columnSortableDisabled(false),
  })
  const shown = count ?? column.count ?? column.items.length
  return (
    <div
      ref={setNodeRef}
      id={droppableType === 'column' ? column.id : undefined}
      data-kanban-collapsed-column={column.id}
      className={cn(
        'flex h-full min-h-[72px] flex-shrink-0 flex-col items-center rounded-t-xl bg-surface-sunken transition-colors duration-150',
        !bare && column.accent && `border-t-4 ${column.accent}`,
        bare && 'mb-2 rounded-md',
        isOver && 'bg-muted ring-2 ring-ring/40',
        COLLAPSED_COLUMN_CLASS,
      )}>
      {bare ? (
        <div className="flex flex-col items-center gap-2 pt-2">
          <span className="text-xs text-muted-foreground">{shown}</span>
          <div className="max-h-[240px] truncate text-xs font-semibold text-muted-foreground [writing-mode:vertical-rl]">
            {column.title}
          </div>
        </div>
      ) : renderCollapsedColumn ? (
        renderCollapsedColumn(column)
      ) : (
        <CollapsedColumnBody
          title={column.title}
          count={shown}
          onExpand={onExpandColumn && (() => onExpandColumn(column.id))}
        />
      )}
    </div>
  )
}

/** Default collapsed-column body: expand button, vertical title, count. */
function CollapsedColumnBody({ title, count, onExpand }: { title?: ReactNode; count: number; onExpand?: () => void }) {
  return (
    <div className="flex flex-col items-center gap-2 pt-2">
      <button
        type="button"
        aria-label="Expand column"
        onClick={onExpand}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <ChevronsLeftRight className="h-4 w-4" />
      </button>
      <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{count}</span>
      {title !== undefined && (
        <div className="max-h-[240px] truncate text-sm font-semibold [writing-mode:vertical-rl]">{title}</div>
      )}
    </div>
  )
}

/** Sideways pointer travel (px) before a mouse press becomes a board pan rather than a click. */
export const BOARD_PAN_ACTIVATION_PX = 6

/** How long after a pan ends the resulting click is still swallowed. */
export const BOARD_PAN_CLICK_GUARD_MS = 50

/** Wheel gesture axis. `null` = no axis chosen yet. */
export type WheelAxis = 'x' | 'y' | null
/** How long a wheel axis stays locked after its last event. */
export const WHEEL_AXIS_LOCK_MS = 100
/** |deltaX| below this is trackpad noise, never a pan on its own. */
export const WHEEL_NOISE_PX = 3
/** A locked axis flips when the other axis is this many times larger… */
export const WHEEL_AXIS_FLIP_RATIO = 2
/** …and at least this large, in the same event. */
export const WHEEL_AXIS_FLIP_PX = 8

/**
 * Pure axis resolution for one wheel event given the current lock. Exported
 * for unit tests.
 *
 * - No lock: horizontal wins when |dx| ≥ |dy| and clears the noise floor;
 *   otherwise any vertical movement is vertical; pure sub-noise dx is ignored.
 * - Locked: the locked axis holds, so a steady gesture never flickers, UNLESS
 *   the event is decisively on the other axis (FLIP_RATIO × and ≥ FLIP_PX),
 *   which is a new gesture and flips the lock at once. This is what lets a
 *   sideways swipe that carries 1px of deltaY break out of a vertical lock.
 */
export function resolveWheelAxis(lock: WheelAxis, dx: number, dy: number): WheelAxis {
  const ax = Math.abs(dx)
  const ay = Math.abs(dy)
  if (lock === 'x') {
    return ay >= ax * WHEEL_AXIS_FLIP_RATIO && ay >= WHEEL_AXIS_FLIP_PX ? 'y' : 'x'
  }
  if (lock === 'y') {
    return ax >= ay * WHEEL_AXIS_FLIP_RATIO && ax >= WHEEL_AXIS_FLIP_PX ? 'x' : 'y'
  }
  if (ax >= ay && ax >= WHEEL_NOISE_PX) {
    return 'x'
  }
  return ay > 0 ? 'y' : null
}

/**
 * Elements a mouse press must NOT pan the board from. Controls and fields keep
 * their own behavior; `[role="button"]` covers custom controls AND every
 * drag-enabled dnd-kit card (`sortableDragAttributes` strips the role from a
 * drag-disabled one, which may pan): a press on a card is a card drag in any
 * direction, never a pan. When cards panned, a sideways drag from one column
 * to the next scrolled the board instead of moving the card (PROD-1463). The
 * column reorder handle is a real `button`; dialogs / side panels are separate
 * surfaces; `[data-kanban-pan-ignore]` is the opt-out for anything else.
 */
export const BOARD_PAN_IGNORE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'textarea',
  'select',
  'label',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="listbox"]',
  'aside',
  '[data-kanban-pan-ignore]',
].join(',')

/**
 * The dnd-kit MouseSensor constraint: a card drag starts once the pointer has
 * moved 8px in ANY direction. Never add a `tolerance` (dnd-kit cancels the
 * pending drag once the pointer passes it) or a per-axis `distance`: either
 * one turns a sideways drag from one column to the next into a no-op
 * (PROD-1463).
 */
export const MOUSE_ACTIVATION_CONSTRAINT: PointerActivationConstraint = { distance: 8 }

/**
 * Pure gate for the page-wide drag-to-pan. Primary mouse button only (touch
 * pans natively; pen/secondary buttons keep their meaning), the board must
 * actually overflow, and the press must not land on an ignored element,
 * drag-enabled cards included (see `BOARD_PAN_IGNORE_SELECTOR`).
 */
export function shouldStartBoardPan(
  e: Pick<PointerEvent, 'button' | 'pointerType' | 'target'>,
  scroller: Pick<HTMLElement, 'scrollWidth' | 'clientWidth'>,
): boolean {
  if (e.button !== 0 || e.pointerType !== 'mouse') {
    return false
  }
  if (scroller.scrollWidth <= scroller.clientWidth) {
    return false
  }
  const target = e.target as { closest?: (selector: string) => unknown } | null
  if (!target || typeof target.closest !== 'function') {
    return false
  }
  return target.closest(BOARD_PAN_IGNORE_SELECTOR) === null
}

/**
 * Class list for the desktop column's card scroll area. Same contract as
 * `MOBILE_COLUMN_SCROLL_ROOT_CLASS`: the X axis MUST chain. `overflow-y-auto`
 * makes this a scroll container on both axes, and the previous
 * `overscroll-x-none` blocked horizontal scroll chaining from the column to
 * the board scroller — a sideways trackpad or touch gesture that started over
 * a column stopped dead at the column instead of panning the board. Only the
 * Y axis is contained. Exported so the test can pin the X axis open.
 */
export const DESKTOP_COLUMN_SCROLL_ROOT_CLASS =
  'min-h-[50px] flex-1 space-y-2 overflow-y-auto overscroll-y-contain [touch-action:pan-x_pan-y] px-3 pb-3'

interface DesktopColumnProps<T extends { id: string }> {
  column: KanbanColumn<T>
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  columnClassName: string
  showDragHandle: boolean
  disableDrag?: boolean
}

function DesktopColumn<T extends { id: string }>({
  column,
  renderCard,
  emptyState,
  columnClassName,
  showDragHandle,
  disableDrag,
}: DesktopColumnProps<T>) {
  // FACTORY-24: `disabled` must be the object form, never a boolean.
  // `disabled: true` disables `droppable` too and the column drops off
  // dnd-kit's droppable registry — drop-on-empty-column then silently
  // breaks. See `columnSortableDisabled` for the why.
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: { type: 'column' },
    disabled: columnSortableDisabled(showDragHandle),
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const count = column.count ?? column.items.length
  const itemIds = column.items.map((item) => item.id)

  return (
    <div
      ref={setNodeRef}
      style={style}
      id={column.id}
      className={cn(
        'flex h-full flex-shrink-0 flex-col rounded-t-xl bg-surface-sunken transition-colors duration-150',
        column.accent && `border-t-4 ${column.accent}`,
        columnClassName,
      )}>
      <KanbanColumnHeader
        title={column.title}
        icon={column.icon}
        count={count}
        headerExtra={column.headerExtra}
        headerActions={column.headerActions}
        dragAttributes={showDragHandle ? attributes : undefined}
        dragListeners={showDragHandle ? listeners : undefined}
      />
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div data-task-scroll-root="true" className={DESKTOP_COLUMN_SCROLL_ROOT_CLASS}>
          {column.items.length === 0
            ? (column.empty ?? emptyState ?? null)
            : column.items.map((item) => (
                <SortableCard key={item.id} id={item.id} disabled={disableDrag || column.locked}>
                  {(meta) => renderCard(item, meta)}
                </SortableCard>
              ))}
          {column.footer}
        </div>
      </SortableContext>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Mobile layout — snap-scroll + dot indicator                        */
/* ------------------------------------------------------------------ */

interface MobileBoardProps<T extends { id: string }> {
  columns: KanbanColumn<T>[]
  columnIds: string[]
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  activeId: string | null
  mobileColumnWidthRatio: number
  disableDrag?: boolean
  className?: string
}

export interface MobileBoardHandle {
  /** Programmatically scroll the mobile column scroller to a column index. */
  scrollToColumn: (idx: number) => void
}

function MobileBoardInner<T extends { id: string }>(
  { columns, renderCard, emptyState, activeId, mobileColumnWidthRatio, disableDrag, className }: MobileBoardProps<T>,
  ref: Ref<MobileBoardHandle>,
) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [columnWidth, setColumnWidth] = useState(0)
  const [peek, setPeek] = useState(0)
  const GAP = 12

  const updateWidth = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }
    const vw = window.innerWidth
    // Match desktop column width (320 px) when the viewport allows; narrower phones fall back
    // to the ratio so the right-side peek stays visible.
    const col = Math.min(320, Math.floor(vw * mobileColumnWidthRatio))
    setColumnWidth(col)
    // Asana-style left alignment: tiny left gutter, peek lives entirely on the right.
    // `peek` here is just the right-side spacer between the active column and the screen edge.
    setPeek(Math.max(12, vw - col - 16))
  }, [mobileColumnWidthRatio])

  useEffect(() => {
    const raf = requestAnimationFrame(updateWidth)
    window.addEventListener('resize', updateWidth)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', updateWidth)
    }
  }, [updateWidth])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el || columnWidth === 0) {
      return
    }
    const handler = () => {
      const idx = Math.round(el.scrollLeft / (columnWidth + GAP))
      setActiveIndex(Math.max(0, Math.min(columns.length - 1, idx)))
    }
    el.addEventListener('scroll', handler, { passive: true })
    return () => el.removeEventListener('scroll', handler)
  }, [columnWidth, columns.length])

  // Lock page scroll while a card is being dragged so vertical page scroll doesn't fight dnd-kit's
  // auto-scroll. The scroller itself stays scrollable (needed for cross-column drop). Also pin
  // touch-action so iOS doesn't try to interpret the active drag as a swipe / pinch (e2e44753).
  useEffect(() => {
    if (!activeId) {
      return
    }
    const prevOverflow = document.body.style.overflow
    const prevTouch = document.body.style.touchAction
    document.body.style.overflow = 'hidden'
    document.body.style.touchAction = 'none'
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.style.touchAction = prevTouch
    }
  }, [activeId])

  // A finger that lands on the board while it is still moving (mid-swipe
  // momentum, or the snap animation settling on a column) is there to stop the
  // scroll, not to open whatever card happens to be under it. Some WebKit
  // builds still deliver that touch as a tap → click, which opens a task the
  // user never chose and leaves them "stuck" in it. Scroll events don't bubble
  // but do capture, so one listener here sees the board scroller AND every
  // column's vertical scroller; the click is swallowed in the capture phase
  // before it can reach a card. See `isScrollStopTouch`.
  //
  // Keyed on `columnWidth`: until the first width measurement this component
  // renders a placeholder and the scroller does not exist yet, so a mount-only
  // effect would bind to nothing and never retry.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el || columnWidth === 0) {
      return
    }
    let lastScrollAt = Number.NEGATIVE_INFINITY
    let scrollStopTouchAt = Number.NEGATIVE_INFINITY
    const onScroll = () => {
      lastScrollAt = performance.now()
    }
    const onTouchStart = () => {
      // Recomputed on EVERY touch so a stale verdict can never eat a later,
      // legitimate tap.
      const now = performance.now()
      scrollStopTouchAt = isScrollStopTouch(lastScrollAt, now) ? now : Number.NEGATIVE_INFINITY
    }
    const onClick = (e: MouseEvent) => {
      if (!isScrollStopClick(scrollStopTouchAt, performance.now())) {
        return
      }
      scrollStopTouchAt = Number.NEGATIVE_INFINITY
      e.stopPropagation()
      e.preventDefault()
    }
    el.addEventListener('scroll', onScroll, { capture: true, passive: true })
    el.addEventListener('touchstart', onTouchStart, { capture: true, passive: true })
    el.addEventListener('click', onClick, { capture: true })
    return () => {
      el.removeEventListener('scroll', onScroll, { capture: true })
      el.removeEventListener('touchstart', onTouchStart, { capture: true })
      el.removeEventListener('click', onClick, { capture: true })
    }
  }, [columnWidth])

  const scrollTo = useCallback(
    (idx: number) => {
      const el = scrollerRef.current
      if (!el || columnWidth === 0) {
        return
      }
      el.scrollTo({ left: idx * (columnWidth + GAP), behavior: 'smooth' })
    },
    [columnWidth],
  )

  // Expose scrollTo to the parent KanbanBoard so handleDragEnd can settle the
  // viewport on the destination column on success / origin column on failure.
  // Without this, snap-mandatory re-engaging on drop snaps the scroller to
  // whichever column is closest to the wherever-dnd-kit-stopped scrollLeft —
  // typically the origin column on cross-column drops, even though the card
  // landed correctly in the destination.
  useImperativeHandle(ref, () => ({ scrollToColumn: scrollTo }), [scrollTo])

  if (columnWidth === 0) {
    return <div className="h-64" />
  }

  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      <KanbanDotIndicator count={columns.length} activeIndex={activeIndex} onSelect={scrollTo} />
      <div
        ref={scrollerRef}
        className={cn(
          'flex min-h-0 flex-1 gap-3 overflow-x-auto overflow-y-hidden scrollbar-hide',
          // Drop snap entirely while dragging so dnd-kit's autoScroll owns the scroll smoothly.
          // Re-enable snap-mandatory on drop so the board settles on a centered column.
          activeId ? 'snap-none' : 'snap-x snap-mandatory',
        )}
        style={{
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingLeft: 16,
          paddingRight: peek,
          scrollPaddingLeft: 16,
        }}>
        {columns.map((column) => (
          <MobileColumn
            key={column.id}
            column={column}
            renderCard={renderCard}
            emptyState={emptyState}
            width={columnWidth}
            disableDrag={disableDrag}
          />
        ))}
      </div>
    </div>
  )
}

// `forwardRef` doesn't preserve generics natively. Cast through to recover
// the generic `T` so call sites still get type-checked column props.
const MobileBoard = forwardRef(MobileBoardInner) as <T extends { id: string }>(
  props: MobileBoardProps<T> & { ref?: Ref<MobileBoardHandle> },
) => ReturnType<typeof MobileBoardInner>

/**
 * A touch that starts within this many ms of the board's last scroll event
 * landed on a moving board and is a scroll-stop, not a tap.
 */
export const SCROLL_STOP_TAP_GUARD_MS = 100

/** Pure; exported for unit tests. `lastScrollAt` may be -Infinity (never scrolled). */
export function isScrollStopTouch(lastScrollAt: number, touchAt: number): boolean {
  return touchAt - lastScrollAt <= SCROLL_STOP_TAP_GUARD_MS
}

/**
 * The click a scroll-stop touch produces arrives within this many ms of the
 * touch (a tap is ~100ms; the browser adds a little). Anything later is a new
 * interaction and must go through.
 */
export const SCROLL_STOP_CLICK_WINDOW_MS = 700

/** Pure; exported for unit tests. `scrollStopTouchAt` is -Infinity when the last touch was a real tap. */
export function isScrollStopClick(scrollStopTouchAt: number, clickAt: number): boolean {
  return clickAt - scrollStopTouchAt <= SCROLL_STOP_CLICK_WINDOW_MS
}

/**
 * Class list for the mobile column's card scroll area.
 *
 * touch-action allows BOTH axes so horizontal swipes on a card/column body
 * bubble up to the board scroller (overflow-x-auto). An earlier `touch-pan-y`
 * clamped the whole subtree to vertical-only, defeating between-column swipes
 * entirely. Vertical pans still hit the column's overflow-y-auto naturally;
 * pinch/double-tap zoom stays disabled.
 *
 * overscroll-behavior MUST stay `auto` on the X axis. `overflow-y-auto` makes
 * this div a scroll container on BOTH axes, and a non-auto
 * overscroll-behavior-x (`overscroll-contain`, `overscroll-x-none`) blocks
 * scroll CHAINING on X even though the column has nothing to scroll
 * horizontally — a sideways swipe that starts on a card is swallowed here and
 * never reaches the board scroller. On iOS the dead swipe then lands as a tap
 * and opens the card. Only the Y axis is contained (no rubber-band bleed into
 * the page). Exported so the test can pin the X axis open.
 */
export const MOBILE_COLUMN_SCROLL_ROOT_CLASS =
  'min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-y-contain [touch-action:pan-x_pan-y] px-3'

interface MobileColumnProps<T extends { id: string }> {
  column: KanbanColumn<T>
  renderCard: (item: T, meta: { isDragging: boolean }) => ReactNode
  emptyState?: ReactNode
  width: number
  disableDrag?: boolean
}

function MobileColumn<T extends { id: string }>({
  column,
  renderCard,
  emptyState,
  width,
  disableDrag,
}: MobileColumnProps<T>) {
  // Register the column id as a Sortable so empty columns are still drop targets —
  // dnd-kit's collision detector only sees ids that are registered. Draggable is
  // off for mobile (columns are never user-reorderable on phones); droppable must
  // stay on. The object form is load-bearing — see `columnSortableDisabled` and
  // FACTORY-24. (Plain `disabled: true` coerces to `{draggable:true, droppable:true}`
  // and silently breaks drop-on-empty.)
  const { setNodeRef } = useSortable({
    id: column.id,
    data: { type: 'column' },
    disabled: columnSortableDisabled(false),
  })
  const count = column.count ?? column.items.length
  const itemIds = column.items.map((item) => item.id)

  return (
    <div
      ref={setNodeRef}
      className="flex h-full flex-shrink-0 snap-start flex-col rounded-t-xl bg-muted/60"
      style={{ width }}>
      <KanbanColumnHeader
        title={column.title}
        icon={column.icon}
        count={count}
        headerExtra={column.headerExtra}
        headerActions={column.headerActions}
      />
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <div
          data-task-scroll-root="true"
          className={MOBILE_COLUMN_SCROLL_ROOT_CLASS}
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
            WebkitOverflowScrolling: 'touch',
          }}>
          {column.items.length === 0
            ? (column.empty ?? emptyState ?? null)
            : column.items.map((item) => (
                <SortableCard key={item.id} id={item.id} disabled={disableDrag || column.locked}>
                  {(meta) => renderCard(item, meta)}
                </SortableCard>
              ))}
          {column.footer}
        </div>
      </SortableContext>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Shared column header + sortable card + dot indicator               */
/* ------------------------------------------------------------------ */

interface KanbanColumnHeaderProps {
  title: ReactNode
  icon?: ReactNode
  count: number
  headerExtra?: ReactNode
  headerActions?: ReactNode
  dragAttributes?: DraggableAttributes
  dragListeners?: DraggableSyntheticListeners
}

function KanbanColumnHeader({
  title,
  icon,
  count,
  headerExtra,
  headerActions,
  dragAttributes,
  dragListeners,
}: KanbanColumnHeaderProps) {
  return (
    <div className="group relative flex items-center justify-between px-3 pb-2 pt-3">
      {dragListeners && (
        <button
          type="button"
          aria-label="Reorder column"
          {...dragAttributes}
          {...dragListeners}
          className="absolute -left-3 top-1/2 -translate-y-1/2 cursor-grab p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground active:cursor-grabbing group-hover:opacity-100">
          <GripVertical className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="flex min-w-0 items-center gap-1.5">
        {icon}
        <div className="truncate text-sm font-semibold">{title}</div>
        <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{count}</span>
        {headerExtra}
      </div>
      {headerActions && <div className="flex items-center gap-0.5">{headerActions}</div>}
    </div>
  )
}

interface SortableCardProps {
  id: string
  disabled?: boolean
  children: (meta: { isDragging: boolean }) => ReactNode
}

// [Workspaces 5f8fa117] dnd-kit's useSortable `attributes` always carries
// role="button" + tabIndex=0 (defaultRole), even when `disabled`. Spreading it
// onto a card wrapper around interactive content (e.g. a <Link>/<a>) produces
// nested-interactive a11y violations. Strip the interactive attributes when
// drag is off; keep aria-* hints. Pure + exported for unit testing.
export function sortableDragAttributes(
  attributes: Record<string, unknown>,
  disabled: boolean | undefined,
): Record<string, unknown> {
  if (!disabled) {
    return attributes
  }
  const { role: _role, tabIndex: _tabIndex, ['aria-pressed']: _ariaPressed, ...rest } = attributes
  return rest
}

function SortableCard({ id, disabled, children }: SortableCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: 'card' },
    disabled,
  })

  // `touch-action: manipulation` + disabling user-select / touch-callout kills iOS Safari's
  // text-selection magnifier (the floating bubble that appears on long-press of selectable text).
  // The TouchSensor long-press still activates drag; taps still bubble up for card click.
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    touchAction: 'manipulation',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    WebkitTouchCallout: 'none',
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      // The wrapper is focusable (dnd-kit keyboard sensor). The browser's default
      // focus-visible outline is a heavy box that lingers on whichever card was
      // last clicked while the host's own selection moves elsewhere — two
      // highlights. The host renders its own selection state, so no ring here.
      className="rounded-md outline-none focus:outline-none focus-visible:outline-none"
      {...sortableDragAttributes(attributes as unknown as Record<string, unknown>, disabled)}
      {...(disabled ? {} : listeners)}>
      {children({ isDragging })}
    </div>
  )
}

export interface KanbanDotIndicatorProps {
  count: number
  activeIndex: number
  onSelect: (index: number) => void
  className?: string
}

export function KanbanDotIndicator({ count, activeIndex, onSelect, className }: KanbanDotIndicatorProps) {
  if (count <= 1) {
    return null
  }
  return (
    <div className={cn('flex items-center justify-center gap-1.5 py-2', className)}>
      {Array.from({ length: count }).map((_, i) => {
        const active = i === activeIndex
        return (
          <button
            key={i}
            type="button"
            aria-label={`Scroll to column ${i + 1}`}
            onClick={() => onSelect(i)}
            className="-m-2 p-2">
            <span
              className={cn(
                'block rounded-full transition-all duration-200',
                active ? 'h-1.5 w-4 bg-primary' : 'h-1.5 w-1.5 bg-muted-foreground/30',
              )}
            />
          </button>
        )
      })}
    </div>
  )
}
